import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Coins, ShoppingBag, Flower2 } from 'lucide-react'
import { FEEDS, type Feed, type FeedType } from '@/hooks/useAquarium'
import { DECORATIONS, CATEGORY_LABELS, type Decoration } from '@/data/decorations'

interface ShopDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  coins: number
  purchasedDecorations: Set<string>
  onBuyFeed: (feedType: FeedType) => void
  onBuyDecoration: (decorationId: string) => void
}

type TabType = 'feed' | 'decoration'

export function ShopDialog({ open, onOpenChange, coins, purchasedDecorations, onBuyFeed, onBuyDecoration }: ShopDialogProps) {
  const [activeTab, setActiveTab] = useState<TabType>('feed')
  const feeds = Object.values(FEEDS)

  const canBuyFeed = (feed: Feed) => coins >= feed.price
  const canBuyDecoration = (decoration: Decoration) => coins >= decoration.price && !purchasedDecorations.has(decoration.id)

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => onOpenChange(false)}
          />
          <motion.div
            className="fixed top-4 left-4 right-4 bottom-4 z-50 max-w-md mx-auto bg-white/90 backdrop-blur-xl rounded-3xl border border-white/80 shadow-[0_25px_80px_rgba(70,140,220,0.35)] flex flex-col overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
                  <Coins className="text-white" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-black tracking-[0.1em] text-[#2f5d8a]">
                    {activeTab === 'feed' ? '鱼食商店' : '装饰商店'}
                  </h2>
                  <p className="text-xs text-slate-400">
                    {activeTab === 'feed' ? '选择合适的饲料喂养你的鱼' : '为鱼缸增添装饰和效果'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                className="w-9 h-9 rounded-full bg-white/60 border border-white/80 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-white transition-all"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex border-b border-white/50">
              <button
                onClick={() => setActiveTab('feed')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold transition-all ${
                  activeTab === 'feed'
                    ? 'text-[#2f5d8a] border-b-2 border-[#2f5d8a] bg-sky-50/50'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <ShoppingBag size={16} />
                鱼食
              </button>
              <button
                onClick={() => setActiveTab('decoration')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold transition-all ${
                  activeTab === 'decoration'
                    ? 'text-[#2f5d8a] border-b-2 border-[#2f5d8a] bg-sky-50/50'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <Flower2 size={16} />
                装饰
              </button>
            </div>

            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              <AnimatePresence mode="wait">
                {activeTab === 'feed' && (
                  <motion.div
                    key="feed"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="space-y-4"
                  >
                    {feeds.map((feed) => (
                      <motion.button
                        key={feed.id}
                        onClick={() => canBuyFeed(feed) && onBuyFeed(feed.id)}
                        disabled={!canBuyFeed(feed)}
                        className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${
                          canBuyFeed(feed)
                            ? 'bg-white/70 border-white/80 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100/50 active:scale-[0.98]'
                            : 'bg-white/30 border-white/40 opacity-60 cursor-not-allowed'
                        }`}
                        whileHover={canBuyFeed(feed) ? { scale: 1.02 } : {}}
                        whileTap={canBuyFeed(feed) ? { scale: 0.98 } : {}}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                            feed.price === 0
                              ? 'bg-slate-100'
                              : feed.price <= 20
                              ? 'bg-green-100'
                              : feed.price <= 35
                              ? 'bg-blue-100'
                              : 'bg-yellow-100'
                          }`}>
                            {feed.icon}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-[#2f5d8a]">{feed.name}</span>
                              {feed.special && (
                                <span className="px-2 py-0.5 rounded-full text-xs bg-rose-100 text-rose-500 font-medium">
                                  {feed.special}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">{feed.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs">
                              <span className="text-slate-500">
                                饱食 <span className="font-bold text-orange-500">+{feed.effect.satiety}</span>
                              </span>
                              <span className="text-slate-500">
                                心情 <span className="font-bold text-pink-500">+{feed.effect.mood}</span>
                              </span>
                              <span className="text-slate-500">
                                活力 <span className="font-bold text-green-500">+{feed.effect.energy}</span>
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-yellow-50 border border-yellow-200">
                            <Coins className="text-yellow-500" size={14} />
                            <span className={`font-bold ${canBuyFeed(feed) ? 'text-yellow-600' : 'text-slate-400'}`}>
                              {feed.price === 0 ? '免费' : feed.price}
                            </span>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </motion.div>
                )}

                {activeTab === 'decoration' && (
                  <motion.div
                    key="decoration"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-3"
                  >
                    {DECORATIONS.map((decoration) => {
                      const isPurchased = purchasedDecorations.has(decoration.id)
                      const canBuy = canBuyDecoration(decoration)

                      return (
                        <motion.button
                          key={decoration.id}
                          onClick={() => canBuy && onBuyDecoration(decoration.id)}
                          disabled={!canBuy}
                          className={`w-full p-3 rounded-xl border transition-all text-left ${
                            isPurchased
                              ? 'bg-green-50 border-green-200'
                              : canBuy
                              ? 'bg-white/70 border-white/80 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100/50 active:scale-[0.98]'
                              : 'bg-white/30 border-white/40 opacity-60 cursor-not-allowed'
                          }`}
                          whileHover={canBuy ? { scale: 1.02 } : {}}
                          whileTap={canBuy ? { scale: 0.98 } : {}}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-3xl ${
                              isPurchased ? 'bg-green-100' : 'bg-sky-100'
                            }`}>
                              {decoration.emoji}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-[#2f5d8a]">{decoration.name}</span>
                                <span className="text-xs text-slate-400">{CATEGORY_LABELS[decoration.category]}</span>
                                {isPurchased && (
                                  <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-600 font-medium">
                                    已拥有
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-400 mt-0.5">{decoration.description}</p>
                              <div className="flex items-center gap-4 mt-1.5 text-xs">
                                {decoration.effect.moodBonus && (
                                  <span className="text-slate-500">
                                    心情 +{decoration.effect.moodBonus}/天
                                  </span>
                                )}
                                {decoration.effect.energyBonus && (
                                  <span className="text-slate-500">
                                    活力 +{decoration.effect.energyBonus}/天
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full ${
                              isPurchased
                                ? 'bg-green-100 border border-green-200'
                                : 'bg-yellow-50 border border-yellow-200'
                            }`}>
                              <Coins className={`size-14 ${isPurchased ? 'text-green-500' : 'text-yellow-500'}`} />
                              <span className={`font-bold ${isPurchased ? 'text-green-600' : canBuy ? 'text-yellow-600' : 'text-slate-400'}`}>
                                {isPurchased ? '已拥有' : decoration.price}
                              </span>
                            </div>
                          </div>
                        </motion.button>
                      )
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="p-4 bg-gradient-to-r from-sky-50 to-blue-50 border-t border-white/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">当前金币</span>
                <div className="flex items-center gap-1.5">
                  <Coins className="text-yellow-500" size={16} />
                  <span className="text-lg font-black text-yellow-600">{coins}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
